import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { listSpots } from '../api/spots';
import { createSession } from '../api/sessions';
import type { SpotResponse } from '../types/api';
import { SpotSelect } from '../components/SpotSelect';
import { ParticipantPicker } from '../components/sessions/ParticipantPicker';
import { inputClass } from '../components/ui/inputClass';

export function NewSessionPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const preselectedClubId: number | null = typeof (state as { clubId?: unknown })?.clubId === 'number'
  ? (state as { clubId: number }).clubId
  : null;

  const [spots, setSpots] = useState<SpotResponse[]>([]);
  const [name, setName] = useState('');
  const [spotId, setSpotId] = useState('');
  const [startTime, setStartTime] = useState(() => new Date().toISOString().slice(0, 16));
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listSpots()
      .then(setSpots)
      .catch(() => setError('Failed to load spots.'));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !spotId) return;

    setSaving(true);
    setError(null);
    try {
      const result = await createSession({
        name: name.trim(),
        spotId: Number(spotId),
        startTime: new Date(startTime).toISOString(),
        ...(endTime ? { endTime: new Date(endTime).toISOString() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(participantIds.length > 0 ? { participantUserIds: participantIds } : {}),
        ...(preselectedClubId != null ? { clubId: preselectedClubId } : {}),
      });
      navigate(`/sessions/${result.id}`);
    } catch {
      setError('Failed to create session. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/sessions')}
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          ← Sessions
        </button>
      </div>

      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Start a session</h1>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Session name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Morning bass session"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Spot <span className="text-red-500">*</span>
          </label>
          <SpotSelect spots={spots} value={spotId} onChange={setSpotId} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Start time <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            className={inputClass}
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            End time <span className="text-slate-400 dark:text-slate-500 font-normal">— optional</span>
          </label>
          <input
            type="datetime-local"
            className={inputClass}
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Notes <span className="text-slate-400 dark:text-slate-500 font-normal">— optional</span>
          </label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any notes about this session…"
          />
        </div>

        {preselectedClubId != null && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Participants
            </label>
            <ParticipantPicker
              clubId={preselectedClubId}
              selectedIds={participantIds}
              onChange={setParticipantIds}
            />
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => navigate('/sessions')}
            className="border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || !spotId || saving}
            className="bg-teal-600 dark:bg-teal-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saving ? 'Starting…' : 'Start session'}
          </button>
        </div>
      </form>
    </div>
  );
}
